
import MenuItem from "@/models/MenuItem";
import { defineComponent, ref, watch, computed } from "vue";
import { RouteLocationNormalizedLoaded, RouteMeta, useRoute } from "vue-router";
import useUser from "@/composables/useUser";

export default defineComponent({
  setup() {
    const route = useRoute();
    return {
      paths: useCrumbs(route),
    };
  },
});

function useCrumbs(route: RouteLocationNormalizedLoaded) {
  const user = useUser().user;
  const menuItems = ref<MenuItem[]>([]);

  // Define all possible menu items (both regular and user menu)
  const allMenuItems = computed(() => {
    const regularMenuItems = user.value?.isSuperAdmin
      ? [
          { label: "Organisationer", to: "/organizations" },
          { label: "Applikationer", to: "/applications" },
          { label: "Profilsøgning", to: "/users" },
          { label: "Logs", to: "/logs" },
          { label: "Indstillinger", to: "/setting" },
        ]
      : user.value?.isUserAdmin
      ? [
          { label: "Organisationer", to: "/organizations" },
          { label: "Applikationer", to: "/applications" },
          { label: "Profilsøgning", to: "/users" },
          { label: "Logs", to: "/logs" },
        ]
      : user.value?.isApplicationAdmin
      ? [
          { label: "Organisationer", to: "/organizations" },
          { label: "Applikationer", to: "/applications" },
          { label: "Profilsøgning", to: "/users" },
          { label: "Logs", to: "/logs" },
        ]
      : user.value?.isAdmin
      ? [{ label: "Logs", to: "/logs" }]
      : [];

    // Add user menu items
    const userMenuItems = [
      { label: "Min profil", to: "/me" },
      { label: "Claims oversigt", to: "/claims-overview" },
    ];

    return [...regularMenuItems, ...userMenuItems];
  });

  watch(
    () => route.path,
    (newPath) => {
      let pathArray = newPath.split("/");
      pathArray.shift();

      // First, try to match against known menu items
      const matchedMenuItem = allMenuItems.value.find(
        (item) => item.to === newPath
      );

      if (matchedMenuItem) {
        // If we found a direct match, create a simple breadcrumb
        menuItems.value = [
          {
            label: "Oversigt",
            to: "/",
            class: "text-muted",
          },
          {
            label: matchedMenuItem.label,
            to: matchedMenuItem.to,
            class: "disabled text-primary",
          },
        ];
      } else {
        // Fall back to the original route-based logic
        menuItems.value = pathArray.reduce(
          (breadCrumbArray: MenuItem[], path: string, idx: number) => {
            const matched = route.matched[idx];
            if (matched && matched.meta.breadcrumb) {
              const to = breadCrumbArray[idx - 1]
                ? breadCrumbArray[idx - 1].to + "/" + path
                : "/" + path;
              breadCrumbArray.push({
                label: readTitle(matched.meta, route, to) ?? path,
                to: to,
                class:
                  idx == pathArray.length - 1
                    ? "disabled text-primary"
                    : "text-muted",
              });
            }

            return breadCrumbArray;
          },
          []
        );

        // Add "Oversigt" to the beginning if not already there
        if (menuItems.value.length > 0 && menuItems.value[0].to !== "/") {
          menuItems.value.unshift({
            label: "Oversigt",
            to: "/",
            class: "text-muted",
          });
        }
      }
    },
    { immediate: true }
  );

  const titles = ref<{ [to: string]: string }>({});

  function readTitle(
    meta: RouteMeta,
    route: RouteLocationNormalizedLoaded,
    to: string
  ) {
    if (titles.value[to] != null) {
      if (isString(meta.breadcrumb)) {
        return meta.breadcrumb;
      }
      return titles.value[to];
    } else if (isString(meta.breadcrumb)) {
      return meta.breadcrumb;
    } else if (isFunction(meta.breadcrumb)) {
      titles.value[to] = meta.breadcrumb(route);
      return meta.breadcrumb(route);
    }
  }

  function isString(
    breadcrumb:
      | string
      | ((route: RouteLocationNormalizedLoaded) => string)
      | undefined
  ): breadcrumb is string {
    return typeof breadcrumb === "string";
  }

  function isFunction(
    breadcrumb:
      | string
      | ((route: RouteLocationNormalizedLoaded) => string)
      | undefined
  ): breadcrumb is (route: RouteLocationNormalizedLoaded) => string {
    return typeof breadcrumb === "function";
  }

  return menuItems;
}
